/**
 * Turning the client's world into a frame.
 *
 * Pure, and deliberately so: it takes a world, a camera and a clock, and
 * returns typed arrays. No canvas, no GL context, no DOM — which means the
 * layering rules, the map-memory dimming and the camera arithmetic are all
 * testable, and the two renderers are left with nothing to do but upload what
 * they are given.
 *
 * Layer order per cell (design spec §3.1): terrain background → terrain glyph
 * → items → actors → effects. Items and effects arrive in later milestones;
 * the ordering is established now so nothing has to be re-sequenced when they
 * do. Actors are not in the grid at all — they are sprites, because they move
 * between cells rather than in them.
 */

import {
  chebyshev,
  skyPhase,
  WEATHER_GLOOM,
  type FireSnapshot,
  type SkySnapshot,
  type WeatherKind,
} from '@annwn/protocol';

import type { ClientWorld } from '../world/client-world.js';
import { interpolate } from '../world/interpolation.js';
import {
  ACTOR_LOOK,
  CURSOR_LOOK,
  dim,
  FIRE_LOOK,
  FLORA_LOOK,
  GRAVE_LOOK,
  mix,
  SIGN_LOOK,
  skyTint,
  tileGlyph,
  TILE_LOOK_BY_ID,
  timeTint,
  tinted,
  UNEXPLORED,
  type Tint,
} from './palette.js';
import type { Rgb, Sprite, TerrainFrame } from './renderer.js';

export interface Viewport {
  /** Canvas size in pixels. */
  readonly width: number;
  readonly height: number;
  readonly cellWidth: number;
  readonly cellHeight: number;
}

export interface Scene {
  readonly terrain: TerrainFrame;
  readonly sprites: readonly Sprite[];
}

/** How many cells are needed to cover the canvas, plus one for the scroll. */
export function gridSize(viewport: Viewport): { columns: number; rows: number } {
  return {
    columns: Math.ceil(viewport.width / viewport.cellWidth) + 1,
    rows: Math.ceil(viewport.height / viewport.cellHeight) + 1,
  };
}

export interface CameraView {
  /** Tile coordinates of the point the camera is centred on. Fractional. */
  readonly x: number;
  readonly y: number;
}

/**
 * The world-pixel coordinate of the canvas's top-left corner.
 *
 * Rounded, not floored: this is the "pixel-snapped camera" of M0-05. A grid
 * drawn on a fractional pixel is a grid that shimmers as it scrolls, and at
 * 16-pixel cells it is extremely visible.
 */
function viewOrigin(camera: CameraView, viewport: Viewport): { x: number; y: number } {
  return {
    x: Math.round(camera.x * viewport.cellWidth + viewport.cellWidth / 2 - viewport.width / 2),
    y: Math.round(camera.y * viewport.cellHeight + viewport.cellHeight / 2 - viewport.height / 2),
  };
}

/**
 * The sky, as a tint (M3-05).
 *
 * Pure and exported so the taste gate — "the valley at dusk in rain looks
 * good" — has something a test can hold onto besides a screenshot: the curve
 * is checkable, and only the *judgement* about it needs an eye.
 */
export function tintFor(sky: SkySnapshot): Tint {
  const base = timeTint(sky.daylight, skyPhase(sky));
  return skyTint(base, WEATHER_GLOOM[sky.weather as WeatherKind] ?? 0);
}

/* -------------------------------------------------------------------------- *
 * Distance falloff (M3-09)
 * -------------------------------------------------------------------------- */

/**
 * How much of the sight radius is drawn at full brightness.
 *
 * A quarter, which is generous on purpose: the tension this ticket is after is
 * about the *edge* of what you can see, and a gradient that starts biting at
 * your own feet reads as a bug in the monitor rather than as distance. Inside
 * this ring nothing is dimmed at all, so the tiles a player is actually acting
 * on — the ones they are about to step onto or swing at — are never ambiguous.
 */
export const FULL_BRIGHT_FRACTION = 0.25;

/**
 * How dark the outermost visible tile gets, as a mix toward night.
 *
 * Deliberately short of the map-memory dim (0.55 toward the same colour, see
 * `dim`): a tile at the edge of sight and a tile you are only remembering must
 * not land on the same grey, or the gradient has destroyed the one distinction
 * the renderer most needs to keep. Far-but-seen stays *readable as present*;
 * remembered reads as absent.
 */
export const EDGE_DARKNESS = 0.45;

/**
 * The shape of the curve between them.
 *
 * Above 1, so the falloff is slow through the middle distance and steep at the
 * end — which is the ticket's "should stay legible mid-range and only really
 * bite near the edge". A linear ramp greys the whole screen evenly and reads as
 * fog rather than as distance.
 *
 * Not much above 1, and that is a correction rather than a first guess. At 2.4
 * the curve was so flat through the middle that half a sight radius out came to
 * under one percent of a mix — arithmetically a gradient and visually nothing,
 * with the entire readable range crammed into the outer fifth. 1.8 keeps the
 * shape the ticket asks for and makes it something a player can actually see.
 */
export const FALLOFF_EXPONENT = 1.8;

/**
 * How dark a tile this far from the eye should be drawn, in `[0, EDGE_DARKNESS]`.
 *
 * Pure, and the whole of the ticket's rule — no canvas, no camera, no colour.
 * `0` is untouched and `EDGE_DARKNESS` is as dark as distance alone ever makes
 * anything.
 *
 * Distance is Chebyshev, like everything else that measures the world: a
 * diagonal step is one step, so the darkened region is the same square the
 * sight radius describes rather than a circle inscribed in it. A round gradient
 * over a square sight radius would leave the corners of what the server sent
 * darker than the rules say they are visible.
 */
export function distanceDarkness(distance: number, sight: number): number {
  if (!Number.isFinite(distance) || !Number.isFinite(sight)) return 0;
  if (sight <= 0) return EDGE_DARKNESS;

  const inner = sight * FULL_BRIGHT_FRACTION;
  if (distance <= inner) return 0;
  if (distance >= sight) return EDGE_DARKNESS;

  const t = (distance - inner) / (sight - inner);
  return EDGE_DARKNESS * t ** FALLOFF_EXPONENT;
}

/**
 * Apply the two ground-level dims in the one order that keeps them independent.
 *
 * Memory and distance are mutually exclusive by construction — a tile beyond
 * sight is `remembered`, and a tile inside it is not — so this is a choice
 * rather than a composition, and saying so in one place is what stops a future
 * caller from stacking them and blacking the edge of the screen out.
 */
function shade(colour: number, remembered: boolean, darkness: number): number {
  if (remembered) return dim(colour);
  return darkness > 0 ? dim(colour, darkness) : colour;
}

/**
 * The darkness for a tile, given where the eye is. `0` when there is no eye —
 * between joining and the sim seating the actor, everything is drawn plainly
 * rather than blacked out.
 */
function darknessAt(
  eye: { readonly x: number; readonly y: number } | null,
  sight: number,
  x: number,
  y: number,
): number {
  if (!eye) return 0;
  return distanceDarkness(chebyshev(eye.x, eye.y, x, y), sight);
}

/* -------------------------------------------------------------------------- *
 * Fire light (RC1-02)
 * -------------------------------------------------------------------------- */

/**
 * How much a fire lifts the dark on its own tile, `[0, 1]`.
 *
 * Not quite `1`: even sitting in the fire, midnight is not noon. Left a shade
 * short so a fire pushes the dark back convincingly without flattening the
 * grade the rest of the screen is drawn with — the fire should read as *warm
 * light in the dark*, not as a hole punched to daylight.
 */
export const FIRE_LIGHT_PEAK = 0.9;

/**
 * The shape of the falloff from the flame to the edge of its reach.
 *
 * Above 1, so the ring is bright close in and fades quickly — a real fire lights
 * what is beside it and gutters out at arm's length, rather than casting an even
 * disc. This is the "transparency dependent on distance" the report asks for.
 */
export const FIRE_LIGHT_FALLOFF = 1.6;

/**
 * Embers light a smaller ring (the snapshot already halves their `radius`) and a
 * dimmer one — this is the second half, the intensity. A banked fire is a glow,
 * not a lantern.
 */
export const FIRE_EMBER_SCALE = 0.55;

/** The colour firelight warms a lit tile toward, and how far at the flame. */
export const FIRELIGHT_COLOUR: Rgb = 0xff9d46;
export const FIRE_WARMTH = 0.22;

/**
 * How brightly the fires light a tile, `[0, FIRE_LIGHT_PEAK]` — brightest at a
 * flame, nothing beyond its reach.
 *
 * Chebyshev distance and the snapshot `radius`, so the lit square matches the
 * sim's own `litAt`/`radiusOf` exactly rather than an inscribed circle. Multiple
 * fires take the brightest contribution, not the sum: two campfires overlapping
 * make the seam between them bright, they do not add to white.
 */
export function fireLightAt(fires: readonly FireSnapshot[], x: number, y: number): number {
  let best = 0;
  for (const fire of fires) {
    if (fire.radius <= 0) continue;
    if (fire.kind === 'hearth' && fire.facing) {
      const vectors = {
        n: [0, -1],
        ne: [1, -1],
        e: [1, 0],
        se: [1, 1],
        s: [0, 1],
        sw: [-1, 1],
        w: [-1, 0],
        nw: [-1, -1],
      } as const;
      const [vx, vy] = vectors[fire.facing];
      const dx = x - fire.x;
      const dy = y - fire.y;
      const forward = dx * vx + dy * vy;
      const sideways = Math.abs(dx * vy - dy * vx);
      if ((dx !== 0 || dy !== 0) && (forward < 0 || sideways > forward + 1)) continue;
    }
    const distance = chebyshev(fire.x, fire.y, x, y);
    if (distance > fire.radius) continue;

    const peak = fire.embers ? FIRE_LIGHT_PEAK * FIRE_EMBER_SCALE : FIRE_LIGHT_PEAK;
    const light = peak * (1 - distance / fire.radius) ** FIRE_LIGHT_FALLOFF;
    if (light > best) best = light;
  }
  return best;
}

/**
 * Draw a ground or sprite colour as the firelight leaves it.
 *
 * Two moves, in the order the sky pass already establishes: the fire cancels
 * that much of the distance-dark and that much of the sky's night-tint (a fire
 * is the one thing on the grid pushing the night back), then warms what is left
 * toward ember-orange. `light` is `0` for anything a fire does not reach, and
 * then this is a straight `tinted(dim(...))` with nothing added.
 */
function litColour(
  base: Rgb,
  remembered: boolean,
  darkness: number,
  tint: Tint,
  light: number,
): Rgb {
  const dark = darkness * (1 - light);
  const skyLifted: Tint =
    light > 0 ? { colour: tint.colour, amount: tint.amount * (1 - light) } : tint;
  const graded = tinted(shade(base, remembered, dark), skyLifted);
  return light > 0 ? mix(graded, FIRELIGHT_COLOUR, FIRE_WARMTH * light) : graded;
}

export function buildScene(
  world: ClientWorld,
  camera: CameraView,
  viewport: Viewport,
  now: number,
  /** Actors to pick out — whoever the hovered log line is about (M2-06). */
  highlighted: ReadonlySet<string> = new Set(),
): Scene {
  const { columns, rows } = gridSize(viewport);
  const origin = viewOrigin(camera, viewport);

  const originTileX = Math.floor(origin.x / viewport.cellWidth);
  const originTileY = Math.floor(origin.y / viewport.cellHeight);

  const glyphs = new Uint16Array(columns * rows);
  const fg = new Uint32Array(columns * rows);
  const bg = new Uint32Array(columns * rows);

  const sky = world.sky;
  const tint = tintFor(sky);

  // Where the light stops. The server culled this very snapshot at exactly this
  // radius, so ground drawn beyond it has to read as *recalled* rather than as
  // seen — otherwise there is a ring of lit, empty grass that a player would
  // reasonably read as "there is nothing there".
  const eye = world.self;

  for (let row = 0; row < rows; row += 1) {
    const tileY = originTileY + row;

    for (let column = 0; column < columns; column += 1) {
      const tileX = originTileX + column;
      const index = row * columns + column;

      // Never seen it: draw absence, not a guess.
      if (!world.isExplored(tileX, tileY)) {
        glyphs[index] = UNEXPLORED.glyph;
        fg[index] = UNEXPLORED.fg;
        bg[index] = UNEXPLORED.bg;
        continue;
      }

      const look = TILE_LOOK_BY_ID[world.tileIdAt(tileX, tileY)] ?? UNEXPLORED;
      const remembered =
        !world.isLive(tileX, tileY) ||
        (eye !== null && chebyshev(eye.x, eye.y, tileX, tileY) > sky.sight);

      // By world position, not by screen position: the tuft of grass stays on
      // the same tile as the viewport scrolls past it (M3-01).
      //
      // Map-memory dimming first, then the sky over the top of it: the two are
      // independent, and composing them in this order is what stops a
      // remembered tile at midnight from being dimmed twice into flat black.
      // Three independent dims, composed in this order and never merged
      // (M3-09). Map memory says "you are not looking at this now"; distance
      // says "this is far away"; the sky says "it is dusk and raining". Each
      // asks a different question, so each is a separate mix — and a remembered
      // tile takes no distance term at all, because it is beyond sight by
      // definition and would otherwise be darkened twice into flat black.
      const darkness = remembered ? 0 : darknessAt(eye, sky.sight, tileX, tileY);

      // A fire lights the ground around it (RC1-02). Only where you are actually
      // looking: a remembered tile is one you are not at, so the fire you left
      // burning there does not light your memory of it.
      const light = remembered ? 0 : fireLightAt(world.fires, tileX, tileY);

      glyphs[index] = tileGlyph(look, tileX, tileY);
      fg[index] = litColour(look.fg, remembered, darkness, tint, light);
      bg[index] = litColour(look.bg, remembered, darkness, tint, light);
    }
  }

  const terrain: TerrainFrame = {
    columns,
    rows,
    glyphs,
    fg,
    bg,
    offsetX: originTileX * viewport.cellWidth - origin.x,
    offsetY: originTileY * viewport.cellHeight - origin.y,
  };

  return {
    terrain,
    sprites: buildSprites(world, origin, viewport, now, highlighted, tint, eye, sky.sight),
  };
}

/**
 * Colour for an actor a log line is hovering over (M2-06).
 *
 * The DOM-overlay-to-renderer hook the ticket asks for: hovering "the wolf
 * tears at Gwion's arm" should tell you *which* wolf, in a fight with four.
 */
const HIGHLIGHT_FG = 0xffffe08a;

function buildSprites(
  world: ClientWorld,
  origin: { x: number; y: number },
  viewport: Viewport,
  now: number,
  highlighted: ReadonlySet<string>,
  /**
   * The sky, applied to sprites as well as to terrain (M3-05).
   *
   * Everything the light touches, or the tint is a filter over the ground with
   * people standing on top of it in full noon colour — which is exactly what a
   * half-applied grade looks like, and worse than none.
   */
  tint: Tint,

  /**
   * Where the viewer is, and how far they can see (M3-09).
   *
   * Sprites take the distance falloff as well as the terrain, and that is the
   * point of the ticket rather than a detail of it: a distant *actor* glyph is
   * the thing that should be hard to read — was that a person or a stag — and
   * darkening only the ground under them would leave a full-brightness `w` on a
   * faded field, which is the opposite of the intended effect.
   */
  eye: { readonly x: number; readonly y: number } | null,
  sight: number,
): Sprite[] {
  const sprites: Sprite[] = [];
  const selfId = world.self?.id;

  const far = (x: number, y: number, colour: number): number =>
    dim(colour, darknessAt(eye, sight, x, y));

  // The sky-graded, fire-lit colour for a sprite on a live tile (RC1-02). The
  // same rule the terrain uses, so a body lying in a fire's ring is lit exactly
  // as the ground it lies on — not left dark on bright grass.
  const lit = (x: number, y: number, colour: Rgb): Rgb =>
    litColour(colour, false, darknessAt(eye, sight, x, y), tint, fireLightAt(world.fires, x, y));

  // Sign under absolutely everything (M3-08): a print is *in* the ground, and
  // anything at all standing on the tile is more important to see than the
  // mark something left there an hour ago.
  //
  // Faded by its own strength as well as by distance, so a trail going cold
  // dims rather than winking out — which is the difference between "this is
  // old" and "this is gone", and the whole of what makes a trail readable as
  // a trail.
  for (const sign of world.signs) {
    const look = SIGN_LOOK[sign.kind as keyof typeof SIGN_LOOK] ?? SIGN_LOOK.print;
    const faded = dim(look.fg, (1 - sign.strength) * 0.7);

    sprites.push({
      glyph: look.glyph,
      fg: lit(sign.x, sign.y, faded),
      x: Math.round(sign.x * viewport.cellWidth - origin.x),
      y: Math.round(sign.y * viewport.cellHeight - origin.y),
    });
  }

  // Plants over sign. A patch you are standing on must not hide you,
  // and a body lying in the bracken must not be hidden by it.
  for (const patch of world.flora) {
    const look = patch.standing === 'picked-over' ? FLORA_LOOK.picked : FLORA_LOOK.standing;
    sprites.push({
      glyph: look.glyph,
      fg: lit(patch.x, patch.y, look.fg),
      x: Math.round(patch.x * viewport.cellWidth - origin.x),
      y: Math.round(patch.y * viewport.cellHeight - origin.y),
    });
  }

  // Fires (M3-05), over the ground detail and under everything that can walk.
  //
  // Drawn at all, which they were not until now: a fire is a `LightSource` in
  // the sim rather than a tile, so a client that only knew about tiles and
  // actors rendered a patch of empty grass where one was burning. A player
  // could lay a fire, read in the log that it had caught, and then have no way
  // to find it again — which also quietly broke M5-02's watch fires, where the
  // whole defence is being able to look at the fold and count them.
  for (const fire of world.fires) {
    // A hearth already has its authored `=` terrain glyph. Its snapshot exists
    // for the glow, not to paint a campfire sprite over the masonry.
    if (fire.kind === 'hearth') continue;
    const look = fire.embers ? FIRE_LOOK.embers : FIRE_LOOK.burning;
    sprites.push({
      glyph: look.glyph,
      // Not tinted by the sky, and it is the only thing on the grid that is
      // not. Everything else dims toward night because the light is failing;
      // a fire *is* the light, and dimming it at dusk would be the renderer
      // saying the one thing pushing the dark back is itself going out.
      fg: far(fire.x, fire.y, look.fg),
      x: Math.round(fire.x * viewport.cellWidth - origin.x),
      y: Math.round(fire.y * viewport.cellHeight - origin.y),
    });
  }

  // Dead animals under the human dead, because a person's body is the one you
  // are meant to notice first.
  for (const carcass of world.carcasses) {
    const look = carcass.stage === 'rotten' ? ACTOR_LOOK.rotten : ACTOR_LOOK.carcass;
    sprites.push({
      glyph: look.glyph,
      fg: lit(carcass.x, carcass.y, look.fg),
      x: Math.round(carcass.x * viewport.cellWidth - origin.x),
      y: Math.round(carcass.y * viewport.cellHeight - origin.y),
    });
  }

  // Bodies next, so anyone standing over one is drawn on top of it. Snapped to
  // the grid rather than interpolated: a corpse is a statement about a tile.
  for (const corpse of world.bodies) {
    sprites.push({
      glyph: ACTOR_LOOK.corpse.glyph,
      fg: lit(corpse.x, corpse.y, ACTOR_LOOK.corpse.fg),
      x: Math.round(corpse.x * viewport.cellWidth - origin.x),
      y: Math.round(corpse.y * viewport.cellHeight - origin.y),
    });
  }

  // Graves (M4-07). Drawn under the bodies in the same pass and never
  // interpolated, for a stronger version of the same reason: a grave is the
  // most permanent thing in the valley. The mound reads as ground rather than
  // as a thing to pick up, which is the honest signal — what is under it is
  // reachable only with a spade.
  for (const grave of world.graves) {
    sprites.push({
      glyph: GRAVE_LOOK.glyph,
      fg: lit(grave.x, grave.y, GRAVE_LOOK.fg),
      x: Math.round(grave.x * viewport.cellWidth - origin.x),
      y: Math.round(grave.y * viewport.cellHeight - origin.y),
    });
  }

  for (const entity of world.entities) {
    // The player's own `@` is drawn from the predicted position, not the
    // server's — that is the whole point of prediction — so it is added by the
    // caller rather than here.
    if (entity.id === selfId) continue;

    const at = interpolate(entity, now);
    // Down beats kind: a fallen wolf and a fallen man are both, first and
    // foremost, a body on the ground — which is the thing the player has to be
    // able to see without reading the log.
    const look = entity.combat?.down
      ? ACTOR_LOOK.down
      : entity.kind === 'creature'
        ? ACTOR_LOOK.creature
        : // Grey-Streaked (M4-07): paled, and visible to everyone. Below `down`
          // in the order because a fallen man is a body first, whatever the
          // cauldron did to him.
          entity.pale
          ? ACTOR_LOOK.pale
          : ACTOR_LOOK.player;

    sprites.push({
      glyph: look.glyph,
      // The highlight is the interface talking rather than the valley, so it
      // is *not* tinted: a log line you are hovering must stay findable in a
      // downpour at midnight, which is exactly when you most need it.
      fg: highlighted.has(entity.id) ? HIGHLIGHT_FG : lit(at.x, at.y, look.fg),
      x: Math.round(at.x * viewport.cellWidth - origin.x),
      y: Math.round(at.y * viewport.cellHeight - origin.y),
    });
  }

  return sprites;
}

/**
 * The look cursor, drawn on a whole tile.
 *
 * Snapped to the grid rather than interpolated: the cursor is a statement
 * about a tile, and one gliding between two of them would be ambiguous about
 * which tile it means.
 */
export function cursorSprite(
  at: { x: number; y: number },
  camera: CameraView,
  viewport: Viewport,
): Sprite {
  const origin = viewOrigin(camera, viewport);
  return {
    glyph: CURSOR_LOOK.glyph,
    fg: CURSOR_LOOK.fg,
    x: at.x * viewport.cellWidth - origin.x,
    y: at.y * viewport.cellHeight - origin.y,
  };
}

/**
 * Which tile a canvas pixel falls on — the other direction through
 * `viewOrigin`, for hover and click (M0-06's mouse path).
 */
export function tileAtPixel(
  pixel: { x: number; y: number },
  camera: CameraView,
  viewport: Viewport,
): { x: number; y: number } {
  const origin = viewOrigin(camera, viewport);
  return {
    x: Math.floor((pixel.x + origin.x) / viewport.cellWidth),
    y: Math.floor((pixel.y + origin.y) / viewport.cellHeight),
  };
}

/**
 * The player's own sprite, from the predicted position.
 *
 * `down` is a parameter rather than read off the world here because this draws
 * from the *predicted* self, which is the one thing the client owns — and the
 * viewer seeing their own body go flat is the single clearest signal that they
 * have stopped being someone who acts (M2-07).
 *
 * Deliberately **not** tinted by the sky (M3-05), alone among the sprites. At
 * midnight the grade takes four fifths of a colour, and a player who could not
 * find their own `@` in the dark would not be experiencing tension, they would
 * be experiencing a bug. You always know where you are standing.
 */
export function selfSprite(
  predicted: { x: number; y: number },
  camera: CameraView,
  viewport: Viewport,
  down = false,
): Sprite {
  const origin = viewOrigin(camera, viewport);
  const look = down ? ACTOR_LOOK.down : ACTOR_LOOK.self;
  return {
    glyph: look.glyph,
    fg: look.fg,
    x: Math.round(predicted.x * viewport.cellWidth - origin.x),
    y: Math.round(predicted.y * viewport.cellHeight - origin.y),
  };
}
